import { Dispatch } from 'react';
import { apiClient, apiErrorHandler, ADMIN_URL } from 'src/app/common/network';
import { getQueryPath } from 'src/app/common/utils';
import {
  QRCodeActivityCategoryListParam,
  QRCodeActivityCategory,
  QRCodeActivityTypeListParam,
  QRCodeActivityType,
  QRActivityReportListParam,
  EStatus,
} from '../types/activity-tracking-types';
import {
  QRCodeActivityCategoryApiResponse,
  QRCodeActivityCategoryApiPaginateList,
  QRCodeActivityTypeApiResponse,
  QRCodeActivityTypeApiPaginateList,
  QRActivityReportApiPaginateList,
} from '../types/activity-tracking-api-types';
import {
  toActivityTypeApiPayload,
  transformQRActivityReportList,
  toQRActivityReportApiParams,
} from '../types/activity-tracking-transform';

const AT_URL = `${ADMIN_URL}/diWiMilo/qr-code`;
const CATEGORY = `${AT_URL}/category`;
const TYPE = `${AT_URL}/type`;
const REPORT = `${AT_URL}/report`;

// ─── QR Code Activity Category ────────────────────────────────────────────────

export const fetchQRCodeActivityCategoryList = async (
  params: QRCodeActivityCategoryListParam,
  dispatch: Dispatch<any>,
) => {
  const queryPath = getQueryPath(`${CATEGORY}/list?`, params);
  return apiClient
    .get<QRCodeActivityCategoryApiPaginateList>(queryPath)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const fetchQRCodeActivityCategoryById = async (id: string, dispatch: Dispatch<any>) => {
  return apiClient
    .get<QRCodeActivityCategoryApiResponse>(`${CATEGORY}/${id}`)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const createQRCodeActivityCategory = async (
  body: Partial<QRCodeActivityCategory>,
  dispatch?: Dispatch<any>,
  signal?: AbortSignal,
) => {
  return apiClient
    .post(`${CATEGORY}/create`, body, { signal })
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const updateQRCodeActivityCategory = async (
  id: string,
  body: Partial<QRCodeActivityCategory>,
  dispatch?: Dispatch<any>,
  signal?: AbortSignal,
) => {
  return apiClient
    .put(`${CATEGORY}/${id}`, body, { signal })
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const updateStatusQRCodeActivityCategory = async (
  id: string,
  body: Partial<QRCodeActivityCategory>,
  dispatch: Dispatch<any>,
  signal?: AbortSignal,
) => {
  return apiClient
    .put(`${CATEGORY}/${id}/update-status`, body, { signal })
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

// ─── QR Code Activity Type ────────────────────────────────────────────────────

export const fetchQRCodeActivityTypeList = async (params: QRCodeActivityTypeListParam, dispatch: Dispatch<any>) => {
  const queryPath = getQueryPath(`${TYPE}/list?`, params);
  return apiClient
    .get<QRCodeActivityTypeApiPaginateList>(queryPath)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const fetchQRCodeActivityTypeById = async (id: string, dispatch: Dispatch<any>) => {
  return apiClient
    .get<QRCodeActivityTypeApiResponse>(`${TYPE}/${id}`)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const createQRCodeActivityType = async (
  body: Partial<QRCodeActivityType>,
  dispatch?: Dispatch<any>,
  signal?: AbortSignal,
) => {
  return apiClient
    .post(`${TYPE}/create`, body, { signal })
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const updateQRCodeActivityType = async (
  id: string,
  body: Partial<QRCodeActivityType>,
  dispatch?: Dispatch<any>,
  signal?: AbortSignal,
) => {
  return apiClient
    .put(`${TYPE}/${id}`, body, { signal })
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const updateStatusQRCodeActivityType = async (
  id: string,
  body: Partial<QRCodeActivityTypeApiResponse>,
  dispatch: Dispatch<any>,
  signal?: AbortSignal,
) => {
  return apiClient
    .put(`${TYPE}/${id}/update-status`, body, { signal })
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

// ─── QR Activity Report ───────────────────────────────────────────────────────

export const fetchQRActivityReportList = async (params: QRActivityReportListParam, dispatch: Dispatch<any>) => {
  const apiParams = toQRActivityReportApiParams(params);
  const queryPath = getQueryPath(`${REPORT}/list?`, apiParams);
  return apiClient
    .get<QRActivityReportApiPaginateList>(queryPath)
    .then((response) => transformQRActivityReportList(response.data))
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const exportQRActivityReport = async (
  params: QRActivityReportListParam,
  dispatch: Dispatch<any>,
  signal?: AbortSignal,
) => {
  const body = toQRActivityReportApiParams(params);
  return apiClient
    .post<Blob>(`${REPORT}/export`, body, { responseType: 'blob' })
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};
